import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Account, AccountStats } from '../../../hooks/useAccounts';

interface AccountDetailModalProps {
  open: boolean;
  account: Account | null;
  onClose: () => void;
  onEdit: (account: Account) => void;
  onArchive: (account: Account) => void;
  onDelete?: (account: Account) => void;
  getStats: (id: string) => Promise<AccountStats>;
  onRecalculate?: (accountId: string) => Promise<void>;
}

const TYPE_LABELS: Record<string, string> = {
  corrente: 'Conta Corrente', digital: 'Conta Digital', poupanca: 'Poupança',
  carteira: 'Carteira', investimento: 'Investimentos', dinheiro: 'Dinheiro em Espécie',
  corretora: 'Corretora', dolar: 'Conta em Dólar', euro: 'Conta em Euro',
  outra_moeda: 'Outra Moeda', cripto: 'Criptoativos',
  internacional: 'Conta Internacional', pj: 'Conta PJ',
};

const TYPE_ICONS: Record<string, string> = {
  corrente: 'ri-bank-line', digital: 'ri-smartphone-line', poupanca: 'ri-safe-line',
  carteira: 'ri-wallet-3-line', investimento: 'ri-stock-line', dinheiro: 'ri-money-dollar-circle-line',
  corretora: 'ri-line-chart-line', dolar: 'ri-currency-line', euro: 'ri-currency-line',
  outra_moeda: 'ri-exchange-line', cripto: 'ri-bit-coin-line',
  internacional: 'ri-global-line', pj: 'ri-building-line',
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  BRL: 'R$', USD: '$', EUR: '€', GBP: '£', ARS: '$', BTC: '₿', ETH: 'Ξ', USDT: '₮',
};

const STATUS_COLORS: Record<string, string> = {
  pago: 'bg-emerald-100 text-emerald-700', pendente: 'bg-amber-100 text-amber-700',
  recebido: 'bg-emerald-100 text-emerald-700', vencido: 'bg-red-100 text-red-700',
  atrasado: 'bg-red-100 text-red-700',
};
const STATUS_LABELS: Record<string, string> = {
  pago: 'Pago', pendente: 'Pendente', recebido: 'Recebido', vencido: 'Vencido', atrasado: 'Atrasado',
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const formatCurrency = (val: number, currency = 'BRL') => {
  if (['BTC', 'ETH', 'USDT'].includes(currency)) {
    return `${CURRENCY_SYMBOLS[currency] ?? currency} ${val.toFixed(8)}`;
  }
  const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
  const iso = currency === 'BRL' ? 'BRL' : currency === 'USD' ? 'USD' : currency === 'EUR' ? 'EUR' : currency === 'GBP' ? 'GBP' : 'BRL';
  try {
    return val.toLocaleString(locale, { style: 'currency', currency: iso });
  } catch {
    return `${CURRENCY_SYMBOLS[currency] ?? currency} ${val.toFixed(2)}`;
  }
};

export const AccountDetailModal = ({ open, account, onClose, onEdit, onArchive, onDelete, getStats, onRecalculate }: AccountDetailModalProps) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  useEffect(() => {
    if (open && account) {
      setStats(null);
      setLoadingStats(true);
      getStats(account.id).then(s => { setStats(s); setLoadingStats(false); });
    }
  }, [open, account, getStats]);

  const handleRecalculate = async () => {
    if (!account || !onRecalculate || isRecalculating) return;
    setIsRecalculating(true);
    try {
      await onRecalculate(account.id);
      // Refresh stats after recalculation
      setLoadingStats(true);
      const s = await getStats(account.id);
      setStats(s);
      setLoadingStats(false);
    } finally {
      setIsRecalculating(false);
    }
  };

  if (!open || !account) return null;

  const icon = TYPE_ICONS[account.type] || 'ri-bank-line';
  const label = TYPE_LABELS[account.type] || 'Conta';
  const currency = account.currency || 'BRL';
  const hasBankDetails = account.bank_code || account.branch_number || account.account_number;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between"
          style={{ backgroundColor: account.color + '15', borderBottom: `1px solid ${account.color}30` }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: account.color + '25' }}>
              <i className={`${icon} text-xl`} style={{ color: account.color }} />
            </div>
            <div>
              <h3 className="text-base font-700 text-gray-900">{account.name}</h3>
              {account.nickname && (
                <p className="text-xs text-gray-400 mb-0.5">{account.nickname}</p>
              )}
              <p className="text-sm text-gray-500">{account.bank || label}</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-black/10 text-gray-500 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Balance row */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center gap-6 mb-3">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Saldo inicial</p>
              <p className="text-sm font-600 text-gray-600">
                {formatCurrency(account.initial_balance, currency)}
              </p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Saldo atual</p>
              <p className={`text-lg font-700 ${(stats?.calculatedBalance ?? account.current_balance) >= 0 ? 'text-gray-900' : 'text-red-500'}`}>
                {loadingStats ? '...' : formatCurrency(stats?.calculatedBalance ?? account.current_balance, currency)}
              </p>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1 text-xs bg-white border border-gray-200 rounded-lg px-2 py-0.5 font-500 text-gray-600">
                <span className="font-700">{currency}</span>
                {CURRENCY_SYMBOLS[currency] && <span>· {CURRENCY_SYMBOLS[currency]}</span>}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 font-500">
                <i className={`${icon} text-xs`} />
                {label}
              </span>
            </div>
          </div>

          {/* Bank details pills */}
          {hasBankDetails && (
            <div className="flex items-center gap-2 flex-wrap">
              {account.bank_code && (
                <span className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-500">
                  Cód. <span className="font-600 text-gray-700">{account.bank_code}</span>
                </span>
              )}
              {account.branch_number && (
                <span className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-500">
                  Ag. <span className="font-600 text-gray-700">{account.branch_number}</span>
                </span>
              )}
              {account.account_number && (
                <span className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-500">
                  Cc. <span className="font-600 text-gray-700">
                    {account.account_number}{account.account_digit ? `-${account.account_digit}` : ''}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        {loadingStats ? (
          <div className="px-6 py-4 flex gap-4">
            {[1,2,3,4].map(i => <div key={i} className="flex-1 h-14 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="px-6 py-4 grid grid-cols-4 gap-3 border-b border-gray-100">
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-xs text-emerald-600 font-500 mb-0.5">Receitas</p>
              <p className="text-sm font-700 text-emerald-700">{formatCurrency(stats?.totalIncome ?? 0, currency)}</p>
              <p className="text-xs text-emerald-500">{stats?.incomeCount ?? 0} lançamentos</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 text-center">
              <p className="text-xs text-red-600 font-500 mb-0.5">Despesas</p>
              <p className="text-sm font-700 text-red-700">{formatCurrency(stats?.totalExpense ?? 0, currency)}</p>
              <p className="text-xs text-red-500">{stats?.expenseCount ?? 0} lançamentos</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center col-span-2">
              <p className="text-xs text-gray-500 font-500 mb-0.5">Última movimentação</p>
              <p className="text-sm font-600 text-gray-700">
                {stats?.lastTransaction ? formatDate(stats.lastTransaction) : 'Nenhuma ainda'}
              </p>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-3 border-b border-gray-100">
            <p className="text-xs font-700 text-gray-500 uppercase tracking-wide">Movimentações recentes</p>
          </div>
          {loadingStats ? (
            <div className="px-6 py-4 space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : (stats?.recentTransactions ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-6">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                <i className="ri-receipt-line text-gray-400 text-lg" />
              </div>
              <p className="text-sm font-600 text-gray-500 mb-1">Nenhuma movimentação</p>
              <p className="text-xs text-gray-400">As transações vinculadas a esta conta aparecerão aqui</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {(stats?.recentTransactions ?? []).map(tx => (
                <div key={tx.id} className="flex items-center justify-between px-6 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      tx.transaction_subtype === 'transferencia'
                        ? 'bg-teal-50'
                        : tx.type === 'income' ? 'bg-emerald-50' : 'bg-red-50'
                    }`}>
                      <i className={`text-sm ${
                        tx.transaction_subtype === 'transferencia'
                          ? 'ri-arrow-left-right-line text-teal-500'
                          : tx.type === 'income' ? 'ri-arrow-down-line text-emerald-500' : 'ri-arrow-up-line text-red-500'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-600 text-gray-800 truncate">{tx.description}</p>
                        {tx.transaction_subtype === 'transferencia' && (
                          <span className="text-[10px] bg-teal-50 text-teal-600 border border-teal-100 px-1.5 py-0.5 rounded-full font-600 whitespace-nowrap shrink-0">PIX / Transf.</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{tx.category} · {formatDate(tx.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${STATUS_COLORS[tx.status] || 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABELS[tx.status] || tx.status}
                    </span>
                    <p className={`text-sm font-700 ${
                      tx.transaction_subtype === 'transferencia'
                        ? 'text-teal-600'
                        : tx.type === 'income' ? 'text-emerald-600' : 'text-red-500'
                    }`}>
                      {tx.transaction_subtype === 'transferencia' ? '⇄ ' : tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount, currency)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        {account.notes && (
          <div className="px-6 py-3 bg-amber-50 border-t border-amber-100">
            <p className="text-xs text-amber-700 font-500 mb-0.5">Observação</p>
            <p className="text-sm text-amber-800">{account.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 space-y-2">
          {/* Row 1: quick actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onClose(); navigate(`/contas/extrato/${account.id}`); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-file-list-3-line" />Extrato completo
            </button>
            {onRecalculate && (
              <button
                onClick={handleRecalculate}
                disabled={isRecalculating || loadingStats}
                title="Recalcular saldo com base nos lançamentos pagos/recebidos"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 border border-gray-200 hover:border-emerald-200 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`ri-refresh-line ${isRecalculating ? 'animate-spin' : ''}`} />
                {isRecalculating ? 'Recalculando...' : 'Recalcular saldo'}
              </button>
            )}
          </div>
          {/* Row 2: date + archive/delete + edit */}
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-400 flex-1 truncate">
              Criada em {new Date(account.created_at).toLocaleDateString('pt-BR')}
            </p>
            {onDelete && (
              <button
                onClick={() => { onClose(); onDelete(account); }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-delete-bin-line" />Excluir
              </button>
            )}
            {account.is_archived ? (
              <span className="text-xs text-amber-600 font-600 bg-amber-50 px-3 py-1.5 rounded-lg">Arquivada</span>
            ) : (
              <button onClick={() => { onClose(); onArchive(account); }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-amber-600 hover:bg-amber-50 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-archive-line" />Arquivar
              </button>
            )}
            <button onClick={() => { onClose(); onEdit(account); }}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap">
              <i className="ri-edit-line" />Editar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
